import { useState, useEffect } from "react";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { 
  Award, Trophy, DollarSign, Users, Plus, Edit, Trash2, Eye,
  CheckCircle, XCircle, Clock, Send, RefreshCw, Gift, Star,
  Building2, Calendar, FileText
} from "lucide-react";

export default function PerformanceAwards({ user }) {
  const [activeTab, setActiveTab] = useState("dashboard");
  const [loading, setLoading] = useState(false);
  
  // Period selection
  const currentDate = new Date();
  const [selectedYear, setSelectedYear] = useState(currentDate.getFullYear());
  
  // Data
  const [dashboard, setDashboard] = useState(null);
  const [awardTypes, setAwardTypes] = useState([]);
  const [nominations, setNominations] = useState([]);
  const [awards, setAwards] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  
  // Dialogs
  const [typeDialog, setTypeDialog] = useState({ open: false, type: null });
  const [nominateDialog, setNominateDialog] = useState({ open: false });
  const [reviewDialog, setReviewDialog] = useState({ open: false, nomination: null });
  const [awardDetailDialog, setAwardDetailDialog] = useState({ open: false, award: null });
  
  // Form state
  const [typeForm, setTypeForm] = useState({
    name: "",
    description: "",
    category: "performance",
    eligibility_period: "monthly",
    bonus_type: "fixed",
    bonus_value: 0,
    criteria: {}
  });
  
  const [nominationForm, setNominationForm] = useState({
    employee_id: "",
    award_type_id: "",
    period_year: currentDate.getFullYear(),
    period_month: currentDate.getMonth() + 1,
    period_quarter: null,
    nomination_reason: "",
    bonus_amount: 0
  });
  
  const [reviewComments, setReviewComments] = useState("");
  const [finalBonus, setFinalBonus] = useState(0);
  const [processing, setProcessing] = useState(false);
  
  const years = Array.from({ length: 5 }, (_, i) => currentDate.getFullYear() - 2 + i);
  const months = [
    { value: 1, label: "January" }, { value: 2, label: "February" }, { value: 3, label: "March" },
    { value: 4, label: "April" }, { value: 5, label: "May" }, { value: 6, label: "June" },
    { value: 7, label: "July" }, { value: 8, label: "August" }, { value: 9, label: "September" },
    { value: 10, label: "October" }, { value: 11, label: "November" }, { value: 12, label: "December" }
  ];
  
  const categories = [
    { value: "performance", label: "Performance Based" },
    { value: "milestone", label: "Milestone Achievement" },
    { value: "recognition", label: "Special Recognition" },
    { value: "custom", label: "Custom" }
  ];
  
  const periods = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "annually", label: "Annually" }
  ];
  
  const bonusTypes = [
    { value: "fixed", label: "Fixed Amount" },
    { value: "percentage", label: "Percentage of Salary" },
    { value: "custom", label: "Custom/Manual" }
  ];
  
  // Check permissions
  const isHR = user?.department === "Human Resources" || user?.access_level === "Admin";
  const canNominate = user?.access_level === "Team Lead" || user?.access_level === "Manager" || user?.access_level === "Admin";
  const canApprove = isHR;

  useEffect(() => {
    fetchDashboard();
    fetchAwardTypes();
    fetchNominations();
    fetchAwards();
    fetchEmployees();
    fetchPendingCount();
  }, [selectedYear]);

  const fetchDashboard = async () => {
    try {
      const res = await api.get(`/awards/dashboard?period_year=${selectedYear}`);
      setDashboard(res.data);
    } catch (error) {
      console.error("Failed to load dashboard");
    }
  };

  const fetchAwardTypes = async () => {
    try {
      const res = await api.get('/award-types');
      setAwardTypes(res.data);
    } catch (error) {
      console.error("Failed to load award types");
    }
  };

  const fetchNominations = async () => {
    try {
      const res = await api.get(`/award-nominations?period_year=${selectedYear}`);
      setNominations(res.data);
    } catch (error) {
      console.error("Failed to load nominations");
    }
  };

  const fetchAwards = async () => {
    try {
      const res = await api.get(`/awards?period_year=${selectedYear}`);
      setAwards(res.data);
    } catch (error) {
      console.error("Failed to load awards");
    }
  };

  const fetchEmployees = async () => {
    try {
      const res = await api.get('/employees-for-kra');
      setEmployees(res.data);
    } catch (error) {
      console.error("Failed to load employees");
    }
  };

  const fetchPendingCount = async () => {
    try {
      const res = await api.get('/award-nominations/pending-count');
      setPendingCount(res.data.pending_count || 0);
    } catch (error) {
      console.error("Failed to load pending count");
    }
  };

  // Award Type CRUD
  const openTypeDialog = (type = null) => {
    if (type) {
      setTypeForm({
        name: type.name || "",
        description: type.description || "",
        category: type.category || "performance",
        eligibility_period: type.eligibility_period || "monthly",
        bonus_type: type.bonus_type || "fixed",
        bonus_value: type.bonus_value || 0,
        criteria: type.criteria || {}
      });
    } else {
      setTypeForm({
        name: "",
        description: "",
        category: "performance",
        eligibility_period: "monthly",
        bonus_type: "fixed",
        bonus_value: 0,
        criteria: {}
      });
    }
    setTypeDialog({ open: true, type });
  };

  const handleSaveType = async () => {
    if (!typeForm.name.trim()) {
      toast.error("Award type name is required");
      return;
    }
    
    setProcessing(true);
    try {
      if (typeDialog.type) {
        await api.put(`/award-types/${typeDialog.type.id}`, typeForm);
        toast.success("Award type updated");
      } else {
        await api.post('/award-types', typeForm);
        toast.success("Award type created");
      }
      setTypeDialog({ open: false, type: null });
      fetchAwardTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save award type");
    } finally {
      setProcessing(false);
    }
  };

  const handleDeleteType = async (typeId) => {
    if (!confirm("Are you sure you want to delete this award type?")) return;
    
    try {
      await api.delete(`/award-types/${typeId}`);
      toast.success("Award type deleted");
      fetchAwardTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete award type");
    }
  };

  // Nomination
  const openNominateDialog = () => {
    setNominationForm({
      employee_id: "",
      award_type_id: "",
      period_year: selectedYear,
      period_month: currentDate.getMonth() + 1,
      period_quarter: null,
      nomination_reason: "",
      bonus_amount: 0
    });
    setNominateDialog({ open: true });
  };

  const handleNominate = async () => {
    if (!nominationForm.employee_id || !nominationForm.award_type_id || !nominationForm.nomination_reason) {
      toast.error("Please fill in all required fields");
      return;
    }
    
    setProcessing(true);
    try {
      await api.post('/award-nominations', nominationForm);
      toast.success("Nomination submitted successfully");
      
      // Reset form first
      setNominationForm({
        employee_id: "",
        award_type_id: "",
        period_year: currentDate.getFullYear(),
        period_month: currentDate.getMonth() + 1,
        period_quarter: null,
        nomination_reason: "",
        bonus_amount: 0
      });
      
      // Close dialog after form reset
      setNominateDialog({ open: false });
      
      // Fetch updated data
      fetchNominations();
      fetchPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit nomination");
    } finally {
      setProcessing(false);
    }
  };

  // Review nomination
  const openReviewDialog = (nomination) => {
    setReviewComments("");
    setFinalBonus(nomination.bonus_amount || 0);
    setReviewDialog({ open: true, nomination });
  };

  const handleApprove = async () => {
    if (!reviewDialog.nomination) return;
    
    setProcessing(true);
    try {
      await api.post(`/award-nominations/${reviewDialog.nomination.id}/approve`, {
        review_comments: reviewComments,
        final_bonus_amount: finalBonus
      });
      toast.success("Nomination approved");
      setReviewDialog({ open: false, nomination: null });
      fetchNominations();
      fetchAwards();
      fetchDashboard();
      fetchPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to approve");
    } finally {
      setProcessing(false);
    }
  };

  const handleReject = async () => {
    if (!reviewDialog.nomination || !reviewComments.trim()) {
      toast.error("Please provide rejection reason");
      return;
    }
    
    setProcessing(true);
    try {
      await api.post(`/award-nominations/${reviewDialog.nomination.id}/reject`, {
        review_comments: reviewComments
      });
      toast.success("Nomination rejected");
      setReviewDialog({ open: false, nomination: null });
      fetchNominations();
      fetchPendingCount();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reject");
    } finally {
      setProcessing(false);
    }
  };

  // Mark award as paid
  const handleMarkPaid = async (awardId) => {
    try {
      await api.post(`/awards/${awardId}/mark-paid`);
      toast.success("Award marked as paid");
      fetchAwards();
      fetchDashboard();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'pending':
        return <Badge className="bg-yellow-500 text-white"><Clock className="h-3 w-3 mr-1" />Pending</Badge>;
      case 'approved':
        return <Badge className="bg-emerald-500 text-white"><CheckCircle className="h-3 w-3 mr-1" />Approved</Badge>;
      case 'rejected':
        return <Badge className="bg-red-500 text-white"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
      case 'paid':
        return <Badge className="bg-blue-500 text-white"><DollarSign className="h-3 w-3 mr-1" />Paid</Badge>;
      case 'cancelled':
        return <Badge variant="outline" className="border-gray-500 text-gray-500">Cancelled</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getCategoryBadge = (category) => {
    switch (category) {
      case 'performance':
        return <Badge className="bg-blue-100 text-blue-800">Performance</Badge>;
      case 'milestone':
        return <Badge className="bg-purple-100 text-purple-800">Milestone</Badge>;
      case 'recognition':
        return <Badge className="bg-amber-100 text-amber-800">Recognition</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Custom</Badge>;
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  return (
    <Layout user={user} title="Performance Awards">
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-md3-headline-medium text-md3-on-surface">Performance Awards</h1>
            <p className="text-md3-body-medium text-md3-on-surface-variant">
              Manage award types, nominations, and bonus tracking
            </p>
          </div>
          <div className="flex items-center gap-3">
            <Select value={selectedYear.toString()} onValueChange={(v) => setSelectedYear(parseInt(v))}>
              <SelectTrigger className="w-28 bg-md3-surface border-md3-outline rounded-md3-sm">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map(y => (
                  <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {canNominate && (
              <Button 
                onClick={openNominateDialog}
                className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary rounded-md3-full"
              >
                <Send className="h-4 w-4 mr-2" />
                Nominate
              </Button>
            )}
          </div>
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-md3-surface-container rounded-md3-full p-1">
            <TabsTrigger value="dashboard" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Trophy className="h-4 w-4 mr-2" />
              Dashboard
            </TabsTrigger>
            {canApprove && (
              <TabsTrigger value="pending" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container relative">
                <Clock className="h-4 w-4 mr-2" />
                Pending Approvals
                {pendingCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {pendingCount}
                  </span>
                )}
              </TabsTrigger>
            )}
            <TabsTrigger value="awards" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
              <Award className="h-4 w-4 mr-2" />
              All Awards
            </TabsTrigger>
            {isHR && (
              <TabsTrigger value="settings" className="rounded-md3-full data-[state=active]:bg-md3-secondary-container">
                <Gift className="h-4 w-4 mr-2" />
                Award Types
              </TabsTrigger>
            )}
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-6 mt-6">
            {dashboard && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Total Awards</p>
                          <p className="text-md3-headline-medium text-md3-on-surface">{dashboard.total_awards}</p>
                        </div>
                        <Trophy className="h-10 w-10 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Total Bonus</p>
                          <p className="text-md3-headline-medium text-emerald-600">{formatCurrency(dashboard.total_bonus)}</p>
                        </div>
                        <DollarSign className="h-10 w-10 text-emerald-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Paid</p>
                          <p className="text-md3-headline-medium text-blue-600">{formatCurrency(dashboard.paid_bonus)}</p>
                        </div>
                        <CheckCircle className="h-10 w-10 text-blue-500" />
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-md3-body-small text-md3-on-surface-variant">Pending Approval</p>
                          <p className="text-md3-headline-medium text-amber-600">{dashboard.pending_nominations}</p>
                        </div>
                        <Clock className="h-10 w-10 text-amber-500" />
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* By Award Type & Top Awardees */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                        <Gift className="h-5 w-5" />
                        By Award Type
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {Object.keys(dashboard.by_award_type || {}).length > 0 ? (
                        <div className="space-y-3">
                          {Object.entries(dashboard.by_award_type).map(([type, data]) => (
                            <div key={type} className="flex items-center justify-between p-2 bg-md3-surface-container rounded-md3-md">
                              <div>
                                <p className="text-md3-body-medium text-md3-on-surface font-medium">{type}</p>
                                <p className="text-md3-body-small text-md3-on-surface-variant">{data.count} awards</p>
                              </div>
                              <span className="text-md3-title-small text-emerald-600">{formatCurrency(data.total_bonus)}</span>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-md3-body-medium text-md3-on-surface-variant text-center py-4">No awards yet</p>
                      )}
                    </CardContent>
                  </Card>

                  <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                    <CardHeader>
                      <CardTitle className="text-md3-title-large text-md3-on-surface flex items-center gap-2">
                        <Star className="h-5 w-5 text-amber-500" />
                        Top Awardees
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {dashboard.top_awardees?.length > 0 ? (
                        <div className="space-y-3">
                          {dashboard.top_awardees.map((emp, idx) => (
                            <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 dark:bg-amber-950/20 rounded-md3-md">
                              <div className="flex items-center gap-2">
                                <span className="text-md3-title-medium text-amber-600 w-6">{idx + 1}.</span>
                                <span className="text-md3-body-medium text-md3-on-surface">{emp.employee_name}</span>
                              </div>
                              <div className="text-right">
                                <p className="text-md3-title-small text-emerald-600">{formatCurrency(emp.total_bonus)}</p>
                                <p className="text-md3-body-small text-md3-on-surface-variant">{emp.count} awards</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-md3-body-medium text-md3-on-surface-variant text-center py-4">No awardees yet</p>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </>
            )}
          </TabsContent>

          {/* Pending Approvals Tab */}
          {canApprove && (
            <TabsContent value="pending" className="space-y-4 mt-6">
              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardHeader>
                  <CardTitle className="text-md3-title-large text-md3-on-surface">Pending Nominations</CardTitle>
                  <CardDescription>Review and approve award nominations</CardDescription>
                </CardHeader>
                <CardContent>
                  {nominations.filter(n => n.status === 'pending').length === 0 ? (
                    <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                      <CheckCircle className="h-12 w-12 mx-auto mb-3 text-emerald-500" />
                      <p>No pending nominations</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Employee</TableHead>
                          <TableHead>Award Type</TableHead>
                          <TableHead>Period</TableHead>
                          <TableHead>Nominated By</TableHead>
                          <TableHead>Bonus</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {nominations.filter(n => n.status === 'pending').map((nom) => (
                          <TableRow key={nom.id}>
                            <TableCell className="font-medium">{nom.employee_name}</TableCell>
                            <TableCell>{nom.award_type_name}</TableCell>
                            <TableCell>
                              {nom.period_month ? `${months.find(m => m.value === nom.period_month)?.label} ` : ''}
                              {nom.period_year}
                            </TableCell>
                            <TableCell>{nom.nominated_by_name}</TableCell>
                            <TableCell className="text-emerald-600 font-medium">{formatCurrency(nom.bonus_amount)}</TableCell>
                            <TableCell className="text-right">
                              <Button size="sm" onClick={() => openReviewDialog(nom)} className="bg-md3-primary text-md3-on-primary rounded-md3-full">
                                <Eye className="h-4 w-4 mr-1" />
                                Review
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}

          {/* All Awards Tab */}
          <TabsContent value="awards" className="space-y-4 mt-6">
            <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
              <CardHeader>
                <CardTitle className="text-md3-title-large text-md3-on-surface">Awards - {selectedYear}</CardTitle>
              </CardHeader>
              <CardContent>
                {awards.length === 0 ? (
                  <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                    <Trophy className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                    <p>No awards for this period</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Employee</TableHead>
                        <TableHead>Award Type</TableHead>
                        <TableHead>Period</TableHead>
                        <TableHead>Bonus</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {awards.map((award) => (
                        <TableRow key={award.id}>
                          <TableCell className="font-medium">{award.employee_name}</TableCell>
                          <TableCell>{award.award_type_name}</TableCell>
                          <TableCell>
                            {award.period_month ? `${months.find(m => m.value === award.period_month)?.label} ` : ''}
                            {award.period_year}
                          </TableCell>
                          <TableCell className="text-emerald-600 font-medium">{formatCurrency(award.bonus_amount)}</TableCell>
                          <TableCell>{getStatusBadge(award.bonus_status)}</TableCell>
                          <TableCell className="text-right">
                            <div className="flex items-center justify-end gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setAwardDetailDialog({ open: true, award })} className="h-8 w-8 p-0 rounded-md3-full">
                                <Eye className="h-4 w-4" />
                              </Button>
                              {isHR && award.bonus_status === 'pending' && (
                                <Button size="sm" variant="ghost" onClick={() => handleMarkPaid(award.id)} className="h-8 w-8 p-0 text-blue-600 rounded-md3-full">
                                  <DollarSign className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Award Types Settings Tab */}
          {isHR && (
            <TabsContent value="settings" className="space-y-4 mt-6">
              <Card className="bg-md3-surface-container-low border-0 shadow-md3-1 rounded-md3-lg">
                <CardHeader className="flex flex-row items-center justify-between">
                  <div>
                    <CardTitle className="text-md3-title-large text-md3-on-surface">Award Types</CardTitle>
                    <CardDescription>Configure award types with criteria and bonus settings</CardDescription>
                  </div>
                  <Button onClick={() => openTypeDialog()} className="bg-md3-primary text-md3-on-primary rounded-md3-full">
                    <Plus className="h-4 w-4 mr-2" />
                    Add Award Type
                  </Button>
                </CardHeader>
                <CardContent>
                  {awardTypes.length === 0 ? (
                    <div className="text-center py-8 text-md3-body-medium text-md3-on-surface-variant">
                      <Gift className="h-12 w-12 mx-auto mb-3 text-md3-outline" />
                      <p>No award types configured</p>
                      <p className="text-md3-body-small mt-1">Add your first award type to get started</p>
                    </div>
                  ) : (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead>Period</TableHead>
                          <TableHead>Bonus Type</TableHead>
                          <TableHead>Default Value</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {awardTypes.map((type) => (
                          <TableRow key={type.id}>
                            <TableCell className="font-medium">{type.name}</TableCell>
                            <TableCell>{getCategoryBadge(type.category)}</TableCell>
                            <TableCell className="capitalize">{type.eligibility_period}</TableCell>
                            <TableCell className="capitalize">{type.bonus_type}</TableCell>
                            <TableCell>
                              {type.bonus_type === 'percentage' ? `${type.bonus_value}%` : formatCurrency(type.bonus_value)}
                            </TableCell>
                            <TableCell>
                              {type.is_active ? (
                                <Badge className="bg-emerald-100 text-emerald-800">Active</Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-500">Inactive</Badge>
                              )}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex items-center justify-end gap-1">
                                <Button size="sm" variant="ghost" onClick={() => openTypeDialog(type)} className="h-8 w-8 p-0 rounded-md3-full">
                                  <Edit className="h-4 w-4" />
                                </Button>
                                <Button size="sm" variant="ghost" onClick={() => handleDeleteType(type.id)} className="h-8 w-8 p-0 text-red-600 rounded-md3-full">
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          )}
        </Tabs>
      </div>

      {/* Award Type Dialog */}
      <Dialog open={typeDialog.open} onOpenChange={(open) => setTypeDialog({ ...typeDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-lg">
          <DialogHeader>
            <DialogTitle>{typeDialog.type ? 'Edit Award Type' : 'Add Award Type'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Name *</Label>
              <Input value={typeForm.name} onChange={(e) => setTypeForm({...typeForm, name: e.target.value})} placeholder="e.g., Employee of the Month" />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea value={typeForm.description} onChange={(e) => setTypeForm({...typeForm, description: e.target.value})} placeholder="Award description..." />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select value={typeForm.category} onValueChange={(v) => setTypeForm({...typeForm, category: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {categories.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Eligibility Period</Label>
                <Select value={typeForm.eligibility_period} onValueChange={(v) => setTypeForm({...typeForm, eligibility_period: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {periods.map(p => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Bonus Type</Label>
                <Select value={typeForm.bonus_type} onValueChange={(v) => setTypeForm({...typeForm, bonus_type: v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {bonusTypes.map(b => <SelectItem key={b.value} value={b.value}>{b.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Default Value</Label>
                <Input type="number" value={typeForm.bonus_value} onChange={(e) => setTypeForm({...typeForm, bonus_value: parseFloat(e.target.value) || 0})} />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTypeDialog({ open: false, type: null })}>Cancel</Button>
            <Button onClick={handleSaveType} disabled={processing} className="bg-md3-primary text-md3-on-primary">
              {processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : null}
              {typeDialog.type ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Nominate Dialog */}
      <Dialog open={nominateDialog.open} onOpenChange={(open) => {
        if (!open) {
          // Reset form when closing
          setNominationForm({
            employee_id: "",
            award_type_id: "",
            period_year: currentDate.getFullYear(),
            period_month: currentDate.getMonth() + 1,
            period_quarter: null,
            nomination_reason: "",
            bonus_amount: 0
          });
        }
        setNominateDialog({ open });
      }}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-lg">
          <DialogHeader>
            <DialogTitle>Nominate for Award</DialogTitle>
            <DialogDescription>Submit a nomination for approval by HR</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Employee *</Label>
              <Select value={nominationForm.employee_id} onValueChange={(v) => setNominationForm({...nominationForm, employee_id: v})}>
                <SelectTrigger><SelectValue placeholder="Select employee" /></SelectTrigger>
                <SelectContent>
                  {employees.map(e => (
                    <SelectItem key={e.id} value={e.employee_id || e.id}>{e.first_name} {e.last_name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Award Type *</Label>
              <Select value={nominationForm.award_type_id} onValueChange={(v) => {
                const type = awardTypes.find(t => t.id === v);
                setNominationForm({
                  ...nominationForm, 
                  award_type_id: v,
                  bonus_amount: type?.bonus_value || 0
                });
              }}>
                <SelectTrigger><SelectValue placeholder="Select award type" /></SelectTrigger>
                <SelectContent>
                  {awardTypes.filter(t => t.is_active).map(t => (
                    <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Month</Label>
                <Select value={nominationForm.period_month?.toString()} onValueChange={(v) => setNominationForm({...nominationForm, period_month: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {months.map(m => <SelectItem key={m.value} value={m.value.toString()}>{m.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Year</Label>
                <Select value={nominationForm.period_year.toString()} onValueChange={(v) => setNominationForm({...nominationForm, period_year: parseInt(v)})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {years.map(y => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Nomination Reason *</Label>
              <Textarea value={nominationForm.nomination_reason} onChange={(e) => setNominationForm({...nominationForm, nomination_reason: e.target.value})} placeholder="Why should this employee receive this award?" rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Suggested Bonus Amount</Label>
              <Input type="number" value={nominationForm.bonus_amount} onChange={(e) => setNominationForm({...nominationForm, bonus_amount: parseFloat(e.target.value) || 0})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNominateDialog({ open: false })}>Cancel</Button>
            <Button onClick={handleNominate} disabled={processing} className="bg-md3-primary text-md3-on-primary">
              {processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Send className="h-4 w-4 mr-2" />}
              Submit Nomination
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Nomination Dialog */}
      <Dialog open={reviewDialog.open} onOpenChange={(open) => setReviewDialog({ ...reviewDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-2xl">
          <DialogHeader>
            <DialogTitle>Review Nomination</DialogTitle>
          </DialogHeader>
          {reviewDialog.nomination && (
            <div className="space-y-4">
              <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Employee</p>
                    <p className="text-md3-body-large text-md3-on-surface font-medium">{reviewDialog.nomination.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Award Type</p>
                    <p className="text-md3-body-large text-md3-on-surface font-medium">{reviewDialog.nomination.award_type_name}</p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Nominated By</p>
                    <p className="text-md3-body-medium text-md3-on-surface">{reviewDialog.nomination.nominated_by_name}</p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Suggested Bonus</p>
                    <p className="text-md3-body-large text-emerald-600 font-medium">{formatCurrency(reviewDialog.nomination.bonus_amount)}</p>
                  </div>
                </div>
              </div>
              <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                <p className="text-md3-body-small text-md3-on-surface-variant mb-2">Nomination Reason</p>
                <p className="text-md3-body-medium text-md3-on-surface">{reviewDialog.nomination.nomination_reason}</p>
              </div>
              <div className="space-y-2">
                <Label>Final Bonus Amount</Label>
                <Input type="number" value={finalBonus} onChange={(e) => setFinalBonus(parseFloat(e.target.value) || 0)} />
              </div>
              <div className="space-y-2">
                <Label>Review Comments {reviewDialog.nomination.status === 'pending' ? '(required for rejection)' : ''}</Label>
                <Textarea value={reviewComments} onChange={(e) => setReviewComments(e.target.value)} placeholder="Add your comments..." />
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setReviewDialog({ open: false, nomination: null })}>Cancel</Button>
            <Button variant="outline" onClick={handleReject} disabled={processing} className="border-red-500 text-red-600 hover:bg-red-50">
              <XCircle className="h-4 w-4 mr-2" />
              Reject
            </Button>
            <Button onClick={handleApprove} disabled={processing} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {processing ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle className="h-4 w-4 mr-2" />}
              Approve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Award Detail Dialog */}
      <Dialog open={awardDetailDialog.open} onOpenChange={(open) => setAwardDetailDialog({ ...awardDetailDialog, open })}>
        <DialogContent className="bg-md3-surface-container-high rounded-md3-xl max-w-lg">
          <DialogHeader>
            <DialogTitle>Award Details</DialogTitle>
          </DialogHeader>
          {awardDetailDialog.award && (
            <div className="space-y-4">
              <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Employee</p>
                    <p className="text-md3-body-large text-md3-on-surface font-medium">{awardDetailDialog.award.employee_name}</p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Award Type</p>
                    <p className="text-md3-body-large text-md3-on-surface font-medium">{awardDetailDialog.award.award_type_name}</p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Bonus Amount</p>
                    <p className="text-md3-headline-small text-emerald-600">{formatCurrency(awardDetailDialog.award.bonus_amount)}</p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Status</p>
                    {getStatusBadge(awardDetailDialog.award.bonus_status)}
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Nominated By</p>
                    <p className="text-md3-body-medium text-md3-on-surface">{awardDetailDialog.award.nominated_by_name}</p>
                  </div>
                  <div>
                    <p className="text-md3-body-small text-md3-on-surface-variant">Approved By</p>
                    <p className="text-md3-body-medium text-md3-on-surface">{awardDetailDialog.award.approved_by_name}</p>
                  </div>
                </div>
              </div>
              {awardDetailDialog.award.reason && (
                <div className="p-4 bg-md3-surface-container rounded-md3-lg">
                  <p className="text-md3-body-small text-md3-on-surface-variant mb-2">Reason</p>
                  <p className="text-md3-body-medium text-md3-on-surface">{awardDetailDialog.award.reason}</p>
                </div>
              )}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setAwardDetailDialog({ open: false, award: null })}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
